import "./search-modal.scss";

import * as React from "react";

import {faCircle, faCircleDot} from "@fortawesome/free-regular-svg-icons";
import {faEllipsis, faPencil}  from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}       from "@fortawesome/react-fontawesome";
import {css, cx}               from "emotion";
import {History}               from "history";

import {AnyAction}                                                              from "@Common/react/any-action";
import {myListingsEnabled}                                                      from "@Common/logic/my-listings-enabled"
import {ComponentLink, ComponentNext, Link, makeChain, valueHelper as value}    from "@Common/react/cst2";
import {RangeLoader}                                                            from "@Common/react/range-loader";
import {scopedState}                                                            from "@Common/react/scoped-state";
import {Static2}                                                                from "@Common/react/static-2";
import {UseEffect}                                                              from "@Common/react/use-effect";
import {UseState}                                                               from "@Common/react/use-state";
import {Types}                                                                  from "@Common/types";
import {getPageSummary}                                                         from "@Common/ui-components/heavy/common/design2/data-table/get-page-summary";
import {PageList}                                                               from "@Common/ui-components/heavy/common/design2/pagination-ui/page-list/page-list";
import {PageSize}                                                               from "@Common/ui-components/heavy/common/design2/pagination-ui/page-size/page-size";
import {ModalService3}                                                          from "@Common/ui-components/modal-service-3/modal-service-3";
import {SearchableSelect}                                                       from "@Common/ui-components/searchable-select/searchable-select";
import {MEDIA_QUERY_MD, ViewportTest}                                           from "@Common/ui-components/viewport-test";
import {buildUrlQuery}                                                          from "@Common/utils/build-url-query";
import {filterValues}                                                           from "@Common/utils/objects";

import {consumeContext}             from "@Xpresso/loaders/consume-context";
import {mapMouseEventAsPosition}    from "@Xpresso/routes/account/common/mobile-card/dropdown/dropdown";
import {ModalLayout}                from "@Xpresso/routes/common/modal-layout/modal-layout";

type Listing                = Types.Apis.Payloads.Listings.Listings.Like.Response['records'][number];
type ListingsApiResponse    = Types.Apis.Payloads.Listings.Listings.Like.Response;
type SearchModalNext        = Types.Xpresso.Account.Admin.Listings.SearchModal.Next;
type SearchModalParams      = Types.Xpresso.Account.Admin.Listings.SearchModal.Params;

interface SearchModel {
    city          : string,
    mlsId         : string,
    state         : string,
    streetAddress : string,
    zip           : string,
}

const STATE_VALUES                       = (
    (require("@Common/ui-components/heavy/common/us-states.json") as Types.Common.UiComponents.Heavy.Common.UsStates).map(state => state.abbreviation)
);
const SEARCH_FORM_INIT_VALUE:SearchModel = (
    {city: "", mlsId: "", state: null, streetAddress: "", zip: ""}
);

export const SearchModal = ({
    next: rootNext,
}:{
    next: (s:SearchModalNext) => any,
}) => (
    makeChain<React.ReactNode>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("session"))
    .then(...consumeContext("company"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("routerHistory"))

    .then("modal", ({}, next:ComponentLink<typeof ModalService3<SearchModalParams>>) => (
        ModalService3({next, rootNext, urlParam: "search-existing", width: 850, xdQaName: "Search Modal"})
    ))
    .then("searchParamsState", ({}, next:ComponentLink<typeof UseState<Partial<SearchModel>>>) => (
        UseState({next, initValue: null})
    ))
    .then("paginationState", ({}, next:ComponentLink<typeof UseState<{index: number, size: number}>>) => (
        UseState({next, initValue: {index: 0, size: 5}})
    ))
    .then("listingsRangeLoader", ({
        apis,
        searchParamsState: {value: searchParams},
        paginationState: {value: {index: pageIndex, size: pageSize}},
    }, next:ComponentLink<typeof RangeLoader<ListingsApiResponse, Listing>>) => RangeLoader({
        next,
        _key           : JSON.stringify({searchParams}),
        range          : {from: pageSize * pageIndex, to: pageSize * (pageIndex + 1)},
        load           : ({from, to}) => apis.listings.getListingsLike({
            ...searchParams && filterValues(searchParams, Boolean),
            limit : to - from,
            skip  : from,
            sort  : "agentLastName ASC",
        }),
        mapLoadAsItems : (response) => response?.records ?? [],
    }))
    .then("listingsDisplayed", value(({listingsRangeLoader}) => (
        listingsRangeLoader.loading
        ? []
        : listingsRangeLoader.getItemsInRange()
    )))
    .then("listingsTotalCount", value(({listingsRangeLoader}) => (
        listingsRangeLoader.loading
        ? 0
        : listingsRangeLoader.list[0].totalCount
    )))

    .then("searchFormState", ({}, next:ComponentLink<typeof UseState<SearchModel>>) => (
        UseState({next, initValue: SEARCH_FORM_INIT_VALUE})
    ))
    .then("searchFormRef", ({}, next:ComponentLink<typeof Static2<HTMLFormElement>>) => Static2({next}))
    .then("searchFormIsValid", value(({
        searchFormRef: {get: getSearchForm},
        searchFormState: {value: searchForm}
    }) => (
        getSearchForm()?.checkValidity()
        && (
            Boolean(searchForm.city)
            || Boolean(searchForm.mlsId)
            || Boolean(searchForm.state)
            || Boolean(searchForm.streetAddress)
            || Boolean(searchForm.zip)
        )
    )))
    .then("makeField", value(({searchFormState}) => makeFieldFactory(searchFormState)))

    .then("ResultsViewService", ({}, next:Link<JSX.Element, React.FunctionComponent<ResultsViewProps>>) => (
        <ViewportTest
            if={MEDIA_QUERY_MD}
            then={() => next(CardListResultsService)}
            else={() => next(TableResultsService)}
        />
    ))
    .then("resultsView", ({
        ResultsViewService,
        company,
        listingsDisplayed,
        listingsRangeLoader: {loading},
        listingsTotalCount,
        paginationState,
        routerHistory,
        searchParamsState: {value: searchParams},
        session,
        userLevels,
    }, next:ResultsViewLink) => (
        <ResultsViewService
            busy={loading}
            hasSearched={!!searchParams}
            listingsDisplayed={listingsDisplayed}
            listingsTotalCount={listingsTotalCount}
            onCreateListing={() => goToListingForm(searchParams, routerHistory)}
            onEditListing={listing => goToListingForm(listing, routerHistory)}
            onMarketListing={
                myListingsEnabled({company, session, userLevels})
                && (async (listing) => {
                    await session.applyEmulation({
                        branchId : listing.idBranch,
                        agentId  : listing.idAgent,
                    });
                    routerHistory.push(
                        `/listings?search=${encodeURIComponent(listing.mlsId || listing.streetAddress)}`
                    );
                })
            }
            paginationState={paginationState}
            next={next}
        />
    ))

    .then(({resultsView: {modalButtons}}, next:Link<JSX.Element>) => ModalLayout({
        title   : "Search for an existing listing",
        content : next(),
        buttons : modalButtons,
    }))
    .then(({
        listingsRangeLoader: {loading},
        listingsTotalCount,
        makeField,
        paginationState,
        searchFormIsValid,
        searchFormRef: {set: setSearchFormRef},
        searchFormState,
        searchParamsState: {onChange: setSearchParams, value: searchParams},
        resultsView: {Component: ResultsView, reset: resetResultsView},
    }) => (
        <div className="search-modal-9s7d">
            <form ref={setSearchFormRef}><div className="content">
                {makeField({field: "mlsId", label: "MLS #", required: false, widthFactor: 2})}
                {makeField({field: "streetAddress", label: "Listing Address", required: false, widthFactor: 4})}
                {makeField({field: "city", label: "City", required: false, widthFactor: 2.5})}
                {makeField({
                    field       : "state",
                    input       : SearchableSelect<string>({
                        list        : STATE_VALUES,
                        allowClear  : false,
                        isSelected  : (state) => state === searchFormState.value.state,
                        onSelect    : (state) => {searchFormState.onChange({...searchFormState.value, state})},
                        placeholder : "",
                        xdQaName    : "State",
                    }),
                    label       : "State",
                    required    : false,
                    widthFactor : 1.25,
                })}
                {makeField({field: "zip", label: "ZIP Code", required: false, widthFactor: 1.75})}
                <button
                    onClick={() => {
                        if (searchFormIsValid) {
                            paginationState.onChange({index: 0, size: paginationState.value.size});
                            resetResultsView();
                            setSearchParams(searchFormState.value);
                        }
                    }}
                    className={cx({disabled: !searchFormIsValid})}
                    type="button"
                    xd-qa-type="button"
                    xd-qa-name="Search"
                >Search</button>
            </div></form>

            <section className="results">
                <header>
                    <span className="title" xd-qa-type="text" xd-qa-name={searchParams ? "Search Results" : "All Listings"}>{searchParams ? "Search Results" : "All Listings"}</span>
                    <span className="total-listings" xd-qa-type="text" xd-qa-name="Total Listings">{!loading && `(${listingsTotalCount} listings)`}</span>
                    {!loading && searchParams && (
                        <a
                            onClick={() => {
                                paginationState.onChange({index: 0, size: paginationState.value.size});
                                setSearchParams(null);
                                resetResultsView();
                                searchFormState.onChange(SEARCH_FORM_INIT_VALUE);
                            }}
                            xd-qa-type="link"
                            xd-qa-name="Clear Search"
                        >
                            Clear search
                        </a>
                    )}
                </header>
                <ResultsView/>
            </section>
        </div>
    ))
    .run()
);

const ResultsPagination = ({listingsTotalCount, paginationState}:{
    listingsTotalCount  : number,
    paginationState     : ComponentNext<typeof UseState<{index: number, size: number}>>
}) => (
    <div className="pagination-ui-9s7d">
        <PageSize
            sizes={[5, 10, 20, 100]}
            state={scopedState(paginationState, ["size"])}
            pageSizeMessage=""
        />

        <PageList
            pageCount={Math.ceil(listingsTotalCount / paginationState.value.size)}
            state={scopedState(paginationState, ["index"])}
        />

        <span>
            {getPageSummary({
                pageIndex: paginationState.value.index,
                pageSize: paginationState.value.size,
                count: listingsTotalCount
            })}
        </span>
    </div>
);

const NoResultsAlert = ({hasSearched, onCreateListing: emitCreateListing}:{
    hasSearched     : boolean,
    onCreateListing : () => void,
}) => (
    <div className="no-results-alert-9s7d">
        <span>No record(s) found</span>
        <button
            onClick={emitCreateListing}
            xd-qa-type="button"
            xd-qa-name="Create Listing"
        >
            {"Create " + (hasSearched ? "this" : "a") + " Listing"}
        </button>
    </div>
);

type ResultsViewLink    = Link<JSX.Element, {
    modalButtons   ?: Parameters<typeof ModalLayout>[0]['buttons'],
    reset           : () => void,
    Component       : () => JSX.Element,
}>;
type ResultsViewProps   = {
    busy                : boolean,
    hasSearched         : boolean,
    listingsDisplayed   : Listing[],
    listingsTotalCount  : number,
    next                : ResultsViewLink,
    paginationState     : ComponentNext<typeof UseState<{index: number, size: number}>>,
    onCreateListing     : () => void,
    onEditListing      ?: (listing:Listing) => void,
    onMarketListing    ?: (listing:Listing) => void,
};

const CardListResultsService = ({
    busy,
    hasSearched,
    listingsTotalCount,
    listingsDisplayed,
    paginationState,
    onCreateListing     : emitCreateListing,
    onEditListing       : emitEditListing,
    onMarketListing     : emitMarketListing,
    next                : rootNext
}:ResultsViewProps) => (
    makeChain<JSX.Element>()
    .then("actionsDropdown", ({}, next:ComponentLink<typeof CardActionsDropdown>) => (
        CardActionsDropdown({next})
    ))
    .then(({actionsDropdown: {Component: Actions, open: openActions}}) => rootNext({
        reset: () => {},
        Component: () => (
            <section className="card-list-container">
                <Actions/>
                {listingsDisplayed.map((listing) => (
                    <article key={listing.id}>
                        <header>
                            <div className="titles">
                                <div className="title">{listing.streetAddress}</div>
                                <div className="subtitle">
                                    {listing.city}, {listing.state} {listing.zip}
                                </div>
                            </div>
                            <aside>
                                <ul className="details">
                                    <li><button onClick={(mouseEvent) => openActions({mouseEvent, handlers: {
                                        onEdit      : emitEditListing && (() => emitEditListing(listing)),
                                        onMarket    : emitMarketListing && (() => emitMarketListing(listing)),
                                    }})} className="icon">
                                        <FontAwesomeIcon icon={faEllipsis}/>
                                    </button></li>
                                </ul>
                            </aside>
                        </header>
                        <main>
                            <ul className="details">
                                <li>
                                    <div className="label">Name</div>
                                    <span>
                                        {listing.agentFirstName} {listing.agentLastName}
                                    </span>
                                </li>
                                <li>
                                    <div className="label">MLS #</div>
                                    <span>{listing.mlsId}</span>
                                </li>
                            </ul>
                        </main>
                    </article>
                ))}

                {listingsTotalCount === 0 && !busy && (
                    <div className="no-results">
                        <NoResultsAlert hasSearched={hasSearched} onCreateListing={emitCreateListing}/>
                    </div>
                )}

                {listingsTotalCount > 0 && <ResultsPagination {...{listingsTotalCount, paginationState}}/>}

                {busy && (
                    <div className="loading"><span>Loading...</span></div>
                )}
            </section>
        )
    }))
    .run()
);

type CardActionsDropdownHandlers = {onEdit?: () => void, onMarket?: () => void};
const CardActionsDropdown = ({next: rootNext}:{
    next    : Link<JSX.Element, {
        Component   : () => JSX.Element,
        open        : (state:{
            handlers: CardActionsDropdownHandlers,
            mouseEvent: React.MouseEvent<HTMLElement>,
        }) => void,
    }>
}) => (
    makeChain<JSX.Element>()
    .then("state", ({}, next:ComponentLink<typeof UseState<{
        handlers: CardActionsDropdownHandlers,
        position: {right: number, top: number}
    }>>) => (
        UseState({next})
    ))
    .then(({state: {onChange: setState, value: state}}) => rootNext({
        open        : ({handlers, mouseEvent}) => setState({handlers, position: mapMouseEventAsPosition(mouseEvent)}),
        Component   : () => state && (
            <div xd-qa-type="dropdown" xd-qa-name="Listing Actions" className="actions" style={state.position}>
                {state.handlers.onEdit && (
                    <button
                        onClick={state.handlers.onEdit}
                        xd-qa-type="dropdown_item"
                        xd-qa-name="Edit Listing"
                        className="dropdown-item"
                    >
                        <i className="icon"><FontAwesomeIcon icon={faPencil}/></i>
                        <span className="label">Edit Listing</span>
                    </button>
                )}
                {state.handlers.onMarket && (
                    <button
                        onClick={state.handlers.onMarket}
                        xd-qa-type="dropdown_item"
                        xd-qa-name="Edit Listing"
                        className="market-listing dropdown-item"
                    >
                        <span className="label">Market Listing</span>
                    </button>
                )}

                {/*Capture outside clicks to close, setTimeout after handlers*/}
                <AnyAction capture action={() => setTimeout(() => setState(null), 0)}/>
            </div>
        ) || null
    }))
    .run()
);

const TableResultsService = ({
    busy,
    hasSearched,
    listingsDisplayed,
    listingsTotalCount,
    paginationState,
    next                : rootNext,
    onCreateListing     : emitCreateListing,
    onEditListing       : emitEditListing,
    onMarketListing     : emitMarketListing,
}:ResultsViewProps) => (
    makeChain<JSX.Element>()
    .then("chosenListingState", ({}, next:ComponentLink<typeof UseState<Listing>>) => (
        UseState({next, initValue: null})
    ))
    .then(({chosenListingState: {onChange: setChosenListing, value: chosenListing}}, next: Link<JSX.Element>) => (
        <UseEffect runOnMount fn={() =>
        {
            if (!busy && !chosenListing && listingsDisplayed[0]) {
                setChosenListing(listingsDisplayed[0]);
            }
        }} next={next}/>
    ))
    .then(({chosenListingState: {onChange: setChosenListing, value: chosenListing}}) => rootNext({
        modalButtons    : [
            emitEditListing && {
                label     : "Edit Listing",
                className : "search-modal-9s7d",
                onClick   : () => emitEditListing(chosenListing),
            },
            emitMarketListing && {
                label   : "Market Listing",
                primary : true,
                onClick : () => emitMarketListing(chosenListing),
            },
        ].filter(v => v),
        reset           : () => setChosenListing(null),
        Component       : () => (
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th className="col-primary col-text" xd-qa-type="label" xd-qa-name="User">User</th>
                            <th className="col-number" xd-qa-type="label" xd-qa-name="MLS">MLS #</th>
                            <th className="col-text" xd-qa-type="label" xd-qa-name="Street">Street</th>
                            <th className="col-text" xd-qa-type="label" xd-qa-name="City">City</th>
                            <th className="col-number" xd-qa-type="label" xd-qa-name="Zip">Zip</th>
                        </tr>
                    </thead>
                    <tbody>
                        {listingsTotalCount > 0 && listingsDisplayed.map((listing) => (
                            <tr
                                key={listing.id}
                                onClick={() => setChosenListing(listing)}
                                className={cx("result", {active: listing === chosenListing})}
                                xd-qa-type="card"
                                xd-qa-name={listing.mlsId}
                            >
                                <td className="col-ui">
                                    <button
                                        xd-qa-type="button"
                                        xd-qa-name={"Button" + (listing === chosenListing ? " Selected" : "")}
                                    ><FontAwesomeIcon icon={listing === chosenListing ? faCircleDot : faCircle}/></button>
                                </td>
                                <td className="col-primary col-text" xd-qa-type="text" xd-qa-name={"Name"}>
                                    {listing.agentFirstName} {listing.agentLastName}
                                </td>
                                <td className="col-number" xd-qa-type="text" xd-qa-name={"MLS"}>
                                    {listing.mlsId}
                                </td>
                                <td className="col-text" xd-qa-type="text" xd-qa-name={"Address"}>
                                    {listing.streetAddress}
                                </td>
                                <td className="col-text" xd-qa-type="text" xd-qa-name={"State"}>
                                    {`${listing.city}, ${listing.state}`}
                                </td>
                                <td className="col-number" xd-qa-type="text" xd-qa-name={"Zip"}>
                                    {listing.zip}
                                </td>
                            </tr>
                        ))}

                        {listingsTotalCount === 0 && !busy && (
                            <tr className="no-results">
                                <td colSpan={6}>
                                    <NoResultsAlert hasSearched={hasSearched} onCreateListing={emitCreateListing}/>
                                </td>
                            </tr>
                        )}

                        {busy && (
                            <tr className="loading">
                                <td colSpan={6}>
                                    <div><span>Loading...</span></div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {listingsTotalCount > 0 && (
                        <tfoot>
                            <tr>
                                <th colSpan={6}>
                                    <ResultsPagination {...{listingsTotalCount, paginationState}}/>
                                </th>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        ),
    }))
    .run()
);

const goToListingForm = (listing:Partial<Listing>, routerHistory:History) => {
    let params:Partial<Listing>;
    if (listing.id)
        params = {id: listing.id};
    else
        params = listing;
    routerHistory.push("/account/admin/listing-form" + buildUrlQuery(filterValues(params, Boolean)));
};

const makeFieldFactory = (formState:Types.Common.React.UseState.UseStateNext<SearchModel>) => {
    return ({field, input, label, required, widthFactor=1}:{
        field       ?: keyof SearchModel,
        input       ?: JSX.Element,
        label        : string,
        required     : boolean,
        widthFactor ?: number,
    }) => {
        const {onChange: setValue = null, value = null} = scopedState(formState, [field]);
        return (
            <div className={cx("field", {required, filled: value}, css`flex: ${widthFactor} 0 ${50 * widthFactor}px`)}>
                <label>{label}</label>
                {input ?? (
                    <input
                        onChange={(e) => setValue(e.target.value)}
                        xd-qa-type="text_field"
                        xd-qa-name={label}
                        required={required}
                        value={value}
                    />
                )}
            </div>
        );
    }
};

